import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components'
import { useActiveManagementMetersQuery } from '@/features/ActiveManagement/hooks/useActiveManagementMetersQuery'
import { useActiveManagementSubmetersQuery } from '@/features/ActiveManagement/hooks/useActiveManagementSubmetersQuery'
import {
    ActiveManagementFiltersValues,
    INTERVAL_MAX_METERS_DISPLAY_COUNT,
} from '@/utils/constants'
import { IntervalDisplayParam } from '@/hooks'

type IntervalMetersAlertProps = {
    activeFilters: ActiveManagementFiltersValues
    display?: IntervalDisplayParam
}

export const IntervalMetersAlert: React.FC<IntervalMetersAlertProps> = ({
    activeFilters,
    display = 'meters',
}) => {
    const { t } = useTranslation('translations')
    const { data: meters } = useActiveManagementMetersQuery({ activeFilters })
    const { data: subMeters } = useActiveManagementSubmetersQuery({
        activeFilters,
    })

    const selectedMeters = activeFilters.meter
    const selectedSubmeters = activeFilters.subMeter

    const showAlert = useMemo(() => {
        if (display === 'subMeters') {
            if (
                selectedSubmeters &&
                selectedSubmeters.length > INTERVAL_MAX_METERS_DISPLAY_COUNT
            ) {
                return true
            }

            if (
                (!selectedSubmeters || selectedSubmeters.length === 0) &&
                subMeters &&
                subMeters.length > INTERVAL_MAX_METERS_DISPLAY_COUNT
            ) {
                return true
            }

            return false
        }

        if (selectedMeters.length > INTERVAL_MAX_METERS_DISPLAY_COUNT) {
            return true
        }

        if (
            selectedMeters.length === 0 &&
            meters &&
            meters.length > INTERVAL_MAX_METERS_DISPLAY_COUNT
        ) {
            return true
        }

        return false
    }, [display, selectedMeters.length, meters, selectedSubmeters, subMeters])

    if (!showAlert) {
        return null
    }

    const typeLabel =
        display === 'subMeters'
            ? t('active_management.interval.alert_type.subMeters')
            : t('active_management.interval.alert_type.meters')

    return (
        <Alert
            variant="info"
            description={t('active_management.interval.alert', {
                count: INTERVAL_MAX_METERS_DISPLAY_COUNT,
                type: typeLabel,
            })}
        />
    )
}
